import { useCallback, useEffect, useState } from 'react';
import type { CrearProveedor, EditarProveedor, Oficio, Proveedor } from '@matias-asset-os/contracts';
import { t, type Idioma } from '@matias-asset-os/i18n';
import { api, ErrorApi } from '../api.js';
import { Icono } from '../componentes/Iconos.js';

interface Props {
  idioma: Idioma;
  onVolver: () => void;
}

const OFICIOS: Oficio[] = [
  'FONTANERIA',
  'JARDINERIA',
  'CLIMA',
  'PISCINAS',
  'LIMPIEZA',
  'ELECTRICIDAD',
  'OBRA',
  'SEGURIDAD',
  'OTRO',
];

/**
 * Directorio de proveedores. La empresa gestora mantiene aquí a las empresas de
 * mantenimiento que contrata; cada una con su oficio y su contacto. Es dato de
 * organización interna, por eso solo lo ven Dirección y managers, y el backend
 * lo exige (no basta con ocultar el menú).
 */
export function Proveedores({ idioma, onVolver }: Props) {
  const [lista, setLista] = useState<Proveedor[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [archivados, setArchivados] = useState(false);
  const [editando, setEditando] = useState<Proveedor | 'nuevo' | null>(null);

  const cargar = useCallback(() => {
    api
      .proveedores({ archivados })
      .then((r) => setLista(r.proveedores))
      .catch(() => setError(t(idioma, 'error.servidor')));
  }, [archivados, idioma]);

  useEffect(() => {
    cargar();
  }, [cargar]);

  const activos = (lista ?? []).filter((p) => p.activo).length;

  const archivar = (p: Proveedor) => {
    setError(null);
    const accion = p.activo ? api.archivarProveedor(p.id) : api.reactivarProveedor(p.id);
    accion.then(cargar).catch(() => setError(t(idioma, 'error.servidor')));
  };

  return (
    <main>
      <div className="cabecera-seccion">
        <div>
          <h1>{t(idioma, 'proveedores.titulo')}</h1>
          <p>{t(idioma, 'proveedores.resumen', { total: activos })}</p>
        </div>
        <div className="acciones">
          <label className="conmutador-check">
            <input
              type="checkbox"
              checked={archivados}
              onChange={(e) => setArchivados(e.target.checked)}
            />{' '}
            {t(idioma, 'proveedores.verArchivados')}
          </label>
          <button type="button" className="secundario" onClick={onVolver}>
            {t(idioma, 'app.volver')}
          </button>
          <button type="button" onClick={() => setEditando('nuevo')}>
            + {t(idioma, 'proveedores.nuevo')}
          </button>
        </div>
      </div>

      {error && (
        <p className="error" role="alert">
          {error}
        </p>
      )}

      {editando && (
        <FormularioProveedor
          idioma={idioma}
          proveedor={editando === 'nuevo' ? null : editando}
          onGuardado={() => {
            setEditando(null);
            cargar();
          }}
          onCancelar={() => setEditando(null)}
          onError={() => setError(t(idioma, 'error.servidor'))}
        />
      )}

      {lista === null && !error && <p>{t(idioma, 'app.cargando')}</p>}

      {lista !== null && lista.length === 0 && (
        <div className="tarjeta">
          <p>{t(idioma, 'proveedores.vacio')}</p>
        </div>
      )}

      {lista !== null && lista.length > 0 && (
        <div className="rejilla tarjetas">
          {lista.map((p) => (
            <article key={p.id} className="tarjeta" style={{ opacity: p.activo ? 1 : 0.6 }}>
              <div className="tarjeta-cabeza">
                <b>{p.nombre}</b>
                <span className="pastilla info">{t(idioma, `oficio.${p.oficio}`)}</span>
              </div>

              <dl className="datos-proveedor">
                {p.email && (
                  <>
                    <dt>{t(idioma, 'proveedores.email')}</dt>
                    <dd>
                      <a href={`mailto:${p.email}`}>{p.email}</a>
                    </dd>
                  </>
                )}
                {p.telefono && (
                  <>
                    <dt>{t(idioma, 'proveedores.telefono')}</dt>
                    <dd>{p.telefono}</dd>
                  </>
                )}
                {p.zona && (
                  <>
                    <dt>{t(idioma, 'proveedores.zona')}</dt>
                    <dd>{p.zona}</dd>
                  </>
                )}
                {p.nif && (
                  <>
                    <dt>{t(idioma, 'proveedores.nif')}</dt>
                    <dd>{p.nif}</dd>
                  </>
                )}
                {!p.email && !p.telefono && (
                  <dd className="suave">{t(idioma, 'proveedores.sinContacto')}</dd>
                )}
              </dl>

              {p.notas && <p className="suave">{p.notas}</p>}

              <div className="fila-flex" style={{ gap: '0.5rem', marginTop: 'auto' }}>
                {!p.activo && (
                  <span className="pastilla neutra">{t(idioma, 'proveedores.archivado')}</span>
                )}
                <button
                  type="button"
                  className="enlace"
                  onClick={() => setEditando(p)}
                  style={{ marginLeft: 'auto' }}
                >
                  <Icono nombre="lapiz" />
                </button>
                <button type="button" className="enlace" onClick={() => archivar(p)}>
                  {p.activo
                    ? t(idioma, 'proveedores.archivar')
                    : t(idioma, 'proveedores.reactivar')}
                </button>
              </div>
            </article>
          ))}
        </div>
      )}
    </main>
  );
}

function FormularioProveedor({
  idioma,
  proveedor,
  onGuardado,
  onCancelar,
  onError,
}: {
  idioma: Idioma;
  proveedor: Proveedor | null;
  onGuardado: () => void;
  onCancelar: () => void;
  onError: () => void;
}) {
  const [nombre, setNombre] = useState(proveedor?.nombre ?? '');
  const [oficio, setOficio] = useState<Oficio>(proveedor?.oficio ?? 'FONTANERIA');
  const [email, setEmail] = useState(proveedor?.email ?? '');
  const [telefono, setTelefono] = useState(proveedor?.telefono ?? '');
  const [nif, setNif] = useState(proveedor?.nif ?? '');
  const [zona, setZona] = useState(proveedor?.zona ?? '');
  const [notas, setNotas] = useState(proveedor?.notas ?? '');
  const [enviando, setEnviando] = useState(false);

  const guardar = (e: React.FormEvent) => {
    e.preventDefault();
    if (nombre.trim().length < 2) return;
    setEnviando(true);

    // Al editar, un campo vaciado se envía como null para poder borrarlo; al
    // crear, simplemente se omite lo vacío.
    const limpio = (v: string) => v.trim();
    const opc = (v: string) => (limpio(v) === '' ? (proveedor ? null : undefined) : limpio(v));

    const promesa = proveedor
      ? api.editarProveedor(proveedor.id, {
          nombre: limpio(nombre),
          oficio,
          email: opc(email),
          telefono: opc(telefono),
          nif: opc(nif),
          zona: opc(zona),
          notas: opc(notas),
        } as EditarProveedor)
      : api.crearProveedor({
          nombre: limpio(nombre),
          oficio,
          ...(opc(email) ? { email: limpio(email) } : {}),
          ...(opc(telefono) ? { telefono: limpio(telefono) } : {}),
          ...(opc(nif) ? { nif: limpio(nif) } : {}),
          ...(opc(zona) ? { zona: limpio(zona) } : {}),
          ...(opc(notas) ? { notas: limpio(notas) } : {}),
        } as CrearProveedor);

    promesa
      .then(onGuardado)
      .catch((err: unknown) => {
        onError();
        if (!(err instanceof ErrorApi)) console.error(err);
      })
      .finally(() => setEnviando(false));
  };

  return (
    <form className="tarjeta formulario" onSubmit={guardar}>
      <div className="rejilla dos">
        <div className="campo">
          <label htmlFor="prov-nombre">{t(idioma, 'proveedores.nombre')}</label>
          <input
            id="prov-nombre"
            type="text"
            value={nombre}
            onChange={(e) => setNombre(e.target.value)}
            required
            minLength={2}
            maxLength={200}
          />
        </div>
        <div className="campo">
          <label htmlFor="prov-oficio">{t(idioma, 'proveedores.oficio')}</label>
          <select
            id="prov-oficio"
            value={oficio}
            onChange={(e) => setOficio(e.target.value as Oficio)}
          >
            {OFICIOS.map((o) => (
              <option key={o} value={o}>
                {t(idioma, `oficio.${o}`)}
              </option>
            ))}
          </select>
        </div>
        <div className="campo">
          <label htmlFor="prov-email">{t(idioma, 'proveedores.email')}</label>
          <input
            id="prov-email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            maxLength={200}
          />
        </div>
        <div className="campo">
          <label htmlFor="prov-telefono">{t(idioma, 'proveedores.telefono')}</label>
          <input
            id="prov-telefono"
            type="tel"
            value={telefono}
            onChange={(e) => setTelefono(e.target.value)}
            maxLength={60}
          />
        </div>
        <div className="campo">
          <label htmlFor="prov-zona">{t(idioma, 'proveedores.zona')}</label>
          <input
            id="prov-zona"
            type="text"
            value={zona}
            onChange={(e) => setZona(e.target.value)}
            maxLength={120}
          />
        </div>
        <div className="campo">
          <label htmlFor="prov-nif">{t(idioma, 'proveedores.nif')}</label>
          <input
            id="prov-nif"
            type="text"
            value={nif}
            onChange={(e) => setNif(e.target.value)}
            maxLength={60}
          />
        </div>
      </div>
      <div className="campo">
        <label htmlFor="prov-notas">{t(idioma, 'proveedores.notas')}</label>
        <textarea
          id="prov-notas"
          rows={2}
          value={notas}
          onChange={(e) => setNotas(e.target.value)}
          maxLength={2000}
        />
      </div>
      <div className="fila-flex" style={{ gap: '0.5rem' }}>
        <button type="submit" disabled={enviando || nombre.trim().length < 2}>
          {t(idioma, 'proveedores.guardar')}
        </button>
        <button type="button" className="secundario" onClick={onCancelar}>
          {t(idioma, 'proveedores.cancelar')}
        </button>
      </div>
    </form>
  );
}
